// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import styles from './index.module.scss';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { InfoBlock } from 'components/molecules/MyPage/InfoBlock';
import { Field } from 'components/molecules/MyPage/Field';
import { useTranslation } from 'react-i18next';
import { MyPageType } from 'types/mypage';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { useMypageContactInfo } from 'util/hooks/useMypageContactInfo';
import { REGEX_OBJECT } from 'util/ConstantValues';

export const CollectionAdminOperator = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const hasValidUser = !!user.userId && !!user.role;

  const myPage = useMypageContactInfo<MyPageType.CollectionAdminOperator>({
    myPageType: MyPageType.CollectionAdminOperator,
    enabled: hasValidUser,
  });

  return (
    <div className={styles.mypage}>
      <div className={styles.generalInfo}>
        <InfoBlock
          className={styles.accountInfo}
          title={t('my_page.account_info.title')}
        >
          <div className={styles.col}>
            <Field
              label={t('my_page.account_info.account_category')}
              value={t('my_page.account_info.collection')}
            />
            <Field
              label={t('my_page.account_info.account_permissions')}
              value={t('my_page.account_info.admin_operator')}
            />
          </div>
        </InfoBlock>
        <InfoBlock
          className={styles.masterInfo}
          title={t('my_page.collection_admin_operator.master_info.title')}
        >
          <div className={styles.grid}>
            <Field
              label={t(
                'my_page.collection_admin_operator.master_info.company_name'
              )}
              value={myPage.data?.operatorName}
            />
            <Field
              label={t(
                'my_page.collection_admin_operator.master_info.headquarter_location'
              )}
              value={myPage.data?.address}
            />
            <div />
            <Field
              label={t(
                'my_page.collection_admin_operator.master_info.managed_collection_sites_driver'
              )}
              value={`${t(
                'my_page.collection_admin_operator.master_info.site_count',
                {
                  siteCount: normalizeNumber({
                    value: myPage.data?.siteCount || 0,
                  }),
                  manySite: myPage.data?.siteCount === 1 ? '' : 's',
                }
              )} / ${t(
                'my_page.collection_admin_operator.master_info.driver_count',
                {
                  driverCount: normalizeNumber({
                    value: myPage.data?.driverCount || 0,
                  }),
                  manyDriver: myPage.data?.driverCount === 1 ? '' : 's',
                }
              )}`}
            />
            <Field
              label={t('my_page.collection_admin_operator.master_info.email')}
              value={myPage.data?.staffEmail}
              format="email"
              maxLength={100}
              onEdit={(value) =>
                myPage.handleChangeInfo({
                  staffEmail: value,
                  tel: myPage.data?.tel,
                })
              }
            />
            <Field
              label={t(
                'my_page.collection_admin_operator.master_info.phone_number'
              )}
              value={myPage.data?.tel}
              format="phone"
              maxLength={15}
              regex={REGEX_OBJECT.number}
              onEdit={(value) =>
                myPage.handleChangeInfo({
                  staffEmail: myPage.data?.staffEmail,
                  tel: value,
                })
              }
            />
          </div>
        </InfoBlock>
      </div>
      <div className={styles.additionalInfo}>
        <InfoBlock
          title={t('my_page.collection_admin_operator.site_list.title')}
        >
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    {t('my_page.collection_admin_operator.site_list.site_name')}
                  </th>
                  <th>
                    {t('my_page.collection_admin_operator.site_list.address')}
                  </th>
                  <th className={styles.center}>
                    {t(
                      'my_page.collection_admin_operator.site_list.number_of_driver'
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {myPage.data?.sites.length ? (
                  myPage.data.sites.map((site, index) => {
                    return (
                      <tr key={index}>
                        <td className={mergeClasses(styles.bold, styles.light)}>
                          <p title={site.name}>{site.name}</p>
                        </td>
                        <td>
                          <p title={site.address}>{site.address}</p>
                        </td>
                        <td
                          className={mergeClasses(styles.center, styles.green)}
                        >
                          <p>
                            {t(
                              'my_page.collection_admin_operator.master_info.driver_count',
                              {
                                driverCount: site.driverCount ?? 0,
                                manyDriver: site.driverCount === 1 ? '' : 's',
                              }
                            )}
                          </p>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className={styles.noData}>
                      {t('messages.M_008')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfoBlock>

        <InfoBlock
          title={t('my_page.collection_admin_operator.material_list.title')}
        >
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    {t(
                      'my_page.collection_admin_operator.material_list.material_name'
                    )}
                  </th>
                  <th>
                    {t(
                      'my_page.collection_admin_operator.material_list.delivery_site_name'
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {myPage.data?.materials.length ? (
                  myPage.data.materials.map((material, index) => {
                    return (
                      <tr key={index}>
                        <td className={mergeClasses(styles.bold, styles.light)}>
                          <p title={material.name}>{material.name}</p>
                        </td>
                        <td>
                          <p title={material.deliverySiteName}>
                            {material.deliverySiteName}
                          </p>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={2} className={styles.noData}>
                      {t('messages.M_008')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfoBlock>
      </div>
    </div>
  );
};
